import './index.less';
import { setDialogPlusVisible } from './dialogVisibility';
import classNames from 'classnames';
import React, { forwardRef, ReactNode, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import useScreenDesign, { screenType } from '@/Core/utils/hooks/useScreenDesign';
import { debounce } from '@/Core/utils';
import { getMobileCenteredShellPortalRoot } from '@/layout/components/mobileCenteredShell/portal';
import Portal from '@/componentsUI/Portal';
import { useGlobalOpenSearch, useGlobalOpenUserCenter, useGlobalOpenWallet, useMobileLeftSiderSwitch, useRefreshDialog } from '@/store/commonStore';
import EventMgr from '@/utils/EventMgr';
import { DEFAULT_DIALOG_PRESENTATION, DialogPresentation, resolveDialogPresentation } from './dialogPresentation';

const DIALOG_PLUS_PANEL_OFFSET_TRANSFORM = 'translate3d(0, 12px, 0)';
const DIALOG_PLUS_PANEL_REST_TRANSFORM = 'translate3d(0, 0, 0)';
const DIALOG_PLUS_PANEL_TRANSITION =
  'opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)';
export interface DialogPagePlusProps {
  children?: ReactNode | any;
  props?: any;
  key?: string,
  lazyCompoent?: any;
  presentation?: DialogPresentation;
}

export interface DialogLoadLazyProps {
  key: string,
  props?: any,
  presentation?: DialogPresentation;
}

export interface DialogPlusRef {
  push: (params: DialogPagePlusProps) => void;
  pop: () => void;
  closeAll: () => void;
  replace: (path: any, params: DialogLoadLazyProps) => void;
  pushLoadLazy: (path: any, params: DialogLoadLazyProps) => void;
}

export type DialogPlusState = 'NONE' | 'PUSH' | 'POP' | 'CLOSEALl' | 'REPLACE' | 'CLOSESELEF';

export interface IFDialogPlusContext {
  closeSelf?: (key: string) => void;
  showBtnBack?: (key: string) => boolean;
  closeAll?: () => void;
  pop?: () => void;
  push?: (params: DialogPagePlusProps) => void;
  replace?: (path: any, params: DialogLoadLazyProps) => void;
  enableKey: string;
  closeByNames?: (key: string[]) => void;
  stopClose?: () => void;
  useActiveEffect?: (effect: ()=>void,name:string)=>void;
}

export const DialogPlusContext = React.createContext<IFDialogPlusContext>({
  enableKey: '',
});

export function useDialogPlus() {
  const context = useContext(DialogPlusContext);
  return {
    closeSelf: context.closeSelf,
    showBtnBack: context.showBtnBack,
    closeAll: context.closeAll,
    pop: context.pop,
    push: context.push,
    replace: context.replace,
    enableKey: context.enableKey,
    closeByNames: context.closeByNames,
    stopClose: context.stopClose,
    useActiveEffect: context.useActiveEffect
  };
}

function DialogPlus(props, ref) {
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    () => getMobileCenteredShellPortalRoot()
  );
  const [visible, setVisible] = useState<boolean>(false);
  const prefixCls = 'dialog-plus';
  const mobile = useScreenDesign(screenType.mobile);
  const useShellPageLayout = mobile || Boolean(portalContainer);
  const stateRef = useRef<DialogPlusState>('NONE');
  const [update, setUpdate] = useState<any>({});
  const pageRef = useRef<DialogPagePlusProps[]>([]);
  const maskClipRef = useRef<any>(null);
  const maskRef = useRef<HTMLDivElement>(null);
  const backGroundRef = useRef<any>(null);
  const [refreshDialog] = useRefreshDialog();
  const [drawerVisible, setDrawerVisible] = useMobileLeftSiderSwitch()
  const [searchVisible, setSearchVisible] = useGlobalOpenSearch();
  const [walletVisible, setWalletVisible] = useGlobalOpenWallet();
  const [userCentervisible, setUserCenterVisible] = useGlobalOpenUserCenter();
  const [topKey, setTopkey] = useState<string>('');
  const [topPresentation, setTopPresentation] = useState<DialogPresentation>(
    DEFAULT_DIALOG_PRESENTATION
  );

  const getViewportSize = () => {
    const shellPortalRoot = getMobileCenteredShellPortalRoot();

    if (shellPortalRoot) {
      return {
        width: shellPortalRoot.clientWidth || window.innerWidth,
        height: shellPortalRoot.clientHeight || window.innerHeight,
      };
    }

    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  };

  const applyViewportSize = (element: HTMLElement) => {
    const { width, height } = getViewportSize();
    element.style.width = `${width}px`;
    element.style.height = `${height}px`;
    element.style.maxWidth = `${width}px`;
    element.style.maxHeight = `${height}px`;
  };

  const getDialogPresentation = (page?: DialogPagePlusProps) => {
    return resolveDialogPresentation({
      presentation: page?.presentation,
    });
  };

  const getLastPage = () => pageRef.current[pageRef.current.length - 1];

  const updateTopPageState = (page?: DialogPagePlusProps) => {
    setTopkey(page?.key || page?.children?.['key'] || '');
    setTopPresentation(getDialogPresentation(page));
  };

  const syncDialogPresentationAttributes = () => {
    const elements = document.getElementsByClassName('dialog-plus-page');
    for (let i = 0; i < elements.length; ++i) {
      const element = elements.item(i) as HTMLElement | null;
      if (!element) {
        continue;
      }
      element.dataset.dialogPresentation = getDialogPresentation(pageRef.current[i]);
    }
    const activePresentation = getDialogPresentation(getLastPage());
    if (maskRef.current) {
      maskRef.current.dataset.dialogPresentation = activePresentation;
    }
    if (maskClipRef.current) {
      maskClipRef.current.dataset.dialogPresentation = activePresentation;
    }
    setTopPresentation(activePresentation);
  };

  const push = (params: DialogPagePlusProps) => {
    // if (pageRef.current.length) {
    //   for (let i = 0; i < pageRef.current.length; ++i) {
    //     if (pageRef.current[i].children['key'] === params.children['key']) {
    //       return;
    //     }
    //   }
    // }
    stateRef.current = 'PUSH';
    pageRef.current.push({
      ...params,
      presentation: resolveDialogPresentation({
        presentation: params.presentation,
      }),
    });
    setUpdate({});
    setDialogPlusVisible(true);
    setVisible(true);
    updateTopPageState({
      ...params,
      presentation: resolveDialogPresentation({
        presentation: params.presentation,
      }),
    });
  };

  const pushLoadLazy = (path: any, params: DialogLoadLazyProps) => {

    const index = pageRef.current.findIndex((v)=>{
      if(v.key === params.key) {
        return true
      }
    })
    if(index != -1) {
      return
    }

    if (maskClipRef.current) maskClipRef.current.style.transitionDuration = '0.5s';
    //const compoents = lazyLoad(() => import(`${path}`))
    if(!pageRef.current.length && useShellPageLayout && backGroundRef.current){
      backGroundRef.current.style.left = '0px';
    }
    stateRef.current = 'PUSH';
    pageRef.current.push({
      lazyCompoent: path,
      props: params.props || {},
      key: params.key,
      presentation: resolveDialogPresentation({
        presentation: params.presentation,
      }),
    });
    setUpdate({});
    setDialogPlusVisible(true);
    setVisible(true);
    updateTopPageState({
      key: params.key,
      presentation: params.presentation,
    });

    EventMgr.getInstance().dispatchEvent('OPEN_DIALOG_PAGE',params.key)
  }

  const pop = () => {
    if (!pageRef.current?.length) {
      return;
    }
    stateRef.current = 'POP';
    setUpdate({});
    if (backGroundRef.current) backGroundRef.current.style.zIndex = 'unset';
  };

  const closeAll = () => {
    stateRef.current = 'CLOSEALl';
    setUpdate({});

    EventMgr.getInstance().dispatchEvent('CLOSE_DIALOG_PAGE',pageRef.current[pageRef.current.length - 1]?.key)

  };

  const replace = (path: any, params: DialogLoadLazyProps) => {
    if (maskClipRef.current) maskClipRef.current.style.transitionDuration = '0.5s';

    stateRef.current = 'REPLACE';
    //pageRef.current.push(params);
    pageRef.current.push({
      lazyCompoent: path,
      props: params.props || {},
      key: params.key,
      presentation: resolveDialogPresentation({
        presentation: params.presentation,
      }),
    });
    setUpdate({});
    setDialogPlusVisible(true);
    setVisible(true);
    updateTopPageState({
      key: params.key,
      presentation: params.presentation,
    });
  };

  useImperativeHandle<DialogPlusRef, any>(ref, () => {
    return {
      push,
      pop,
      closeAll,
      replace,
      pushLoadLazy
    };
  });

  const handleMaskAnimationEnd = () => {
    const state = stateRef.current;
    if (state === 'REPLACE' && pageRef.current.length > 1) {
      pageRef.current.splice(pageRef.current.length - 1, 1);
      setUpdate({});
    } else if (state === 'POP') {
      pageRef.current.pop();
      setUpdate({ remove: true });
      const lastPage = getLastPage();
      const nextKey = lastPage?.key || lastPage?.children?.["key"];
      updateTopPageState(lastPage);
      activityEffectRef.current?.[nextKey]?.();
    } else if (state === 'CLOSEALl') {
      pageRef.current.length = 0;
      setUpdate({ remove: true });
      updateTopPageState(undefined);
    }

    stateRef.current = 'NONE';
    maskClipRef.current?.removeEventListener('transitionend', handleMaskAnimationEnd);
  };

  const isLazyLoadingPanel = (element: HTMLElement) =>
    element.className.includes('dialog-plus-loading');

  const syncLazyLoadingPanelSize = (element: HTMLElement) => {
    if (isLazyLoadingPanel(element) && useShellPageLayout) {
      applyViewportSize(element);
    }
  };

  const animateDialogPanelIn = (element: HTMLElement) => {
    element.style.display = 'flex';
    syncLazyLoadingPanelSize(element);
    element.style.transition = DIALOG_PLUS_PANEL_TRANSITION;
    element.style.opacity = '0';
    element.style.transform = DIALOG_PLUS_PANEL_OFFSET_TRANSFORM;
    void element.offsetHeight;
    requestAnimationFrame(() => {
      element.style.opacity = '1';
      element.style.transform = DIALOG_PLUS_PANEL_REST_TRANSFORM;
    });
  };

  const animateDialogPanelOut = (element: HTMLElement) => {
    element.style.transition = DIALOG_PLUS_PANEL_TRANSITION;
    element.style.opacity = '0';
    element.style.transform = DIALOG_PLUS_PANEL_OFFSET_TRANSFORM;
  };

  const animateTopPanelIn = (element: HTMLElement) => {
    const handleEnterEnd = (event: TransitionEvent) => {
      if (event.target !== element) {
        return;
      }
      if (maskClipRef.current) maskClipRef.current.style.height = element.clientHeight + 'px';
      if (stateRef.current === 'REPLACE' && pageRef.current.length > 1) {
        pageRef.current.splice(pageRef.current.length - 2, 1);
        setUpdate({});
      }
      stateRef.current = 'NONE';
      element.removeEventListener('transitionend', handleEnterEnd);
    };
    element.addEventListener('transitionend', handleEnterEnd);
    animateDialogPanelIn(element);
  };

  const animateTopPanelOut = (
    elements: HTMLCollectionOf<Element>,
    element: HTMLElement,
    hideMask: boolean
  ) => {
    const handleExitEnd = (event: TransitionEvent) => {
      if (event.target !== element) {
        return;
      }
      if (stateRef.current === 'CLOSEALl') {
        pageRef.current.length = 0;
      } else {
        pageRef.current.pop();
      }
      const lastPage = getLastPage();
      const nextKey = lastPage?.key || lastPage?.children?.['key'];
      updateTopPageState(lastPage);
      activityEffectRef.current?.[nextKey]?.();
      stateRef.current = 'NONE';
      element.removeEventListener('transitionend', handleExitEnd);
      setUpdate({ remove: true });
    };

    if (stateRef.current === 'CLOSEALl') {
      for (let i = 0; i < elements.length - 1; ++i) {
        const hiddenElement = elements.item(i) as HTMLElement | null;
        if (hiddenElement) {
          hiddenElement.style.display = 'none';
        }
      }
    }

    element.addEventListener('transitionend', handleExitEnd);
    animateDialogPanelOut(element);
    if (hideMask) {
      setVisible(false);
    }
  };

  const handleModalCardState = (
    elements: HTMLCollectionOf<Element>,
    element: HTMLElement
  ) => {
    if (stateRef.current === 'PUSH' || stateRef.current === 'REPLACE') {
      animateTopPanelIn(element);
      return;
    }

    if (stateRef.current === 'POP' || stateRef.current === 'CLOSEALl') {
      animateTopPanelOut(elements, element, true);
    }
  };

  /**处理弹窗状态 */
  const handleDialogState = () => {
    maskClipRef.current?.removeEventListener('transitionend', handleMaskAnimationEnd);
    const elements = document.getElementsByClassName('dialog-plus-page');
    const element = elements.item(elements.length - 1) as HTMLElement;
    syncDialogPresentationAttributes();
    if (stateRef.current === 'CLOSESELEF') {
      stateRef.current = 'NONE';
      return;
    }
    if (!element) {
      return;
    }

    if (element.dataset.dialogPresentation === 'modal-card') {
      handleModalCardState(elements, element);
      return;
    }

    if (!useShellPageLayout) {
      if ((stateRef.current === 'PUSH' && elements.length === 1) || (stateRef.current === 'REPLACE' && elements.length === 1)) {
        if (document.body.clientHeight > 500) {
          if (maskClipRef.current) {
            maskClipRef.current.style.width = element.clientWidth + 'px';
            maskClipRef.current.style.height = element.clientHeight + 'px';
            maskClipRef.current.style.marginTop = -element.clientHeight / 2 + 'px';
            maskClipRef.current.style.marginLeft = -element.clientWidth / 2 + 'px';
          }
        }
        animateTopPanelIn(element);
      } else if ((stateRef.current === 'POP' && elements.length === 1) || (stateRef.current === 'CLOSEALl' && elements.length)) {
        animateTopPanelOut(elements, element, true);
      }
    } else {
      if ((stateRef.current === 'PUSH' && elements.length === 1) || (stateRef.current === 'REPLACE' && elements.length === 1)) {
        if (backGroundRef.current) {
          backGroundRef.current.style.left = '0px';
          backGroundRef.current.style.zIndex = 'unset';
        }
        animateTopPanelIn(element);
      } else if ((stateRef.current === 'POP' && elements.length === 1) || (stateRef.current === 'CLOSEALl' && elements.length)) {
        animateTopPanelOut(elements, element, true);
      }
    }

    if ((stateRef.current === 'PUSH' && elements.length > 1) || (stateRef.current === 'REPLACE' && elements.length > 1)) {
      if(!mobile && maskClipRef.current){
        maskClipRef.current.style.transitionDuration = '0.3s';
        maskClipRef.current.style.width = element.clientWidth + 'px';
        maskClipRef.current.style.height = element.clientHeight + 'px';
        maskClipRef.current.style.marginTop = -element.clientHeight / 2 + 'px';
        maskClipRef.current.style.marginLeft = -element.clientWidth / 2 + 'px';
      }

      if(useShellPageLayout && backGroundRef.current){
        backGroundRef.current.style.left = '0px';
        backGroundRef.current.style.zIndex = isLazyLoadingPanel(element) ? '1' : '-1';
      }
      if(useShellPageLayout && isLazyLoadingPanel(element)){
        element.style.zIndex = '2';
      } else {
        element.style.zIndex = '';
      }
      animateTopPanelIn(element);
    } else if (stateRef.current === 'POP' && elements.length > 1) {
      const element2 = elements.item(elements.length - 2) as HTMLElement;

      if (element2 && maskClipRef.current) {
        maskClipRef.current.style.transitionDuration = '0.3s';
        maskClipRef.current.style.width = element2.clientWidth + 'px';
        maskClipRef.current.style.height = element2.clientHeight + 'px';
        maskClipRef.current.style.marginTop = -element2.clientHeight / 2 + 'px';
        maskClipRef.current.style.marginLeft = -element2.clientWidth / 2 + 'px';
      }

      activityEffectRef.current?.[pageRef.current[pageRef.current.length - 2]?.['key']]?.()
      const handleElement = (event: TransitionEvent) => {
        if (event.target !== element) {
          return;
        }
        stateRef.current = 'NONE';
        pageRef.current.pop();
        element.removeEventListener('transitionend', handleElement);
        setUpdate({ remove: true });
        updateTopPageState(getLastPage());
      };
      element.addEventListener('transitionend', handleElement);
      animateDialogPanelOut(element);
    }
  };

  useEffect(() => {
    if (maskClipRef.current) {
      setTimeout(() => {
        handleDialogState();
      }, 0);
    }
  }, [update,refreshDialog]);

  useEffect(()=>{
    if(refreshDialog) {
      if(topPresentation === 'page' && stateRef.current !== 'REPLACE') {
        stateRef.current = 'PUSH';
      }
      //setUpdate({})
    }
  },[refreshDialog, topPresentation])

  useEffect(() => {
    if (maskClipRef.current) {
      if (useShellPageLayout) {
        if (topPresentation === 'page') {
          maskClipRef.current.style.width = '100%';
          maskClipRef.current.style.height = '100%';
          maskClipRef.current.style.marginTop = '0px';
          maskClipRef.current.style.marginLeft = '0px';
        }
      } else {
        const elements = document.getElementsByClassName('dialog-plus-page');
        const element = elements.item(elements.length - 1) as HTMLElement;
        if (element) {
          setTimeout(() => {
            if (maskClipRef.current) {
              maskClipRef.current.style.marginTop = '0px';
              maskClipRef.current.style.marginLeft = '0px';
              if (topPresentation === 'page') {
                maskClipRef.current.style.width = '0px';
                maskClipRef.current.style.height = '0px';
              }
            }
            stateRef.current = 'PUSH';
            setUpdate({});
          }, 400);
        }
      }
    }
  }, [topPresentation, useShellPageLayout, visible]);

  const handleColseSlef = (key?: string) => {
    for (let i = 0; i < pageRef.current.length; ++i) {
      if (pageRef.current[i]?.children?.['key'] === key || pageRef.current[i]?.key === key) {
        if (i === pageRef.current.length - 1) {
          if (pageRef.current[i]['dialogFlag']) {
            return;
          }
          pageRef.current[i]['dialogFlag'] = "destory"
          pop();
        } else {
          pageRef.current.splice(i, 1);
          stateRef.current = 'CLOSESELEF';
          setUpdate({ remove: true });
        }
        return;
      }
    }
  };

  const handleColseByNames = (keys: string[]) => {
    if (pageRef.current.length < keys.length) {
      return
    }
    if (keys.length === pageRef.current.length) {
      closeAll()
      return
    }
    if (keys.length < 2) {
      handleColseSlef(keys[0])
      return
    }
    let flagRemoveTop = false;
    const topKey = pageRef.current[pageRef.current.length - 1]['key'];
    keys.forEach((key: string) => {
      for (let i = 0; i < pageRef.current.length; ++i) {
        if (pageRef.current[i]?.children?.['key'] === key || pageRef.current[i]?.key === key) {
          if (topKey === key) {
            flagRemoveTop = true;
          } else {
            pageRef.current.splice(i, 1);
          }

          return
        }
      }
    })
    if (flagRemoveTop) {
      pop()
    } else {
      stateRef.current = 'CLOSESELEF';
      setUpdate({ remove: true });
    }
  }

  const hadnleShowBtnBack = (key?: string) => {
    if (pageRef.current.length <= 1 || (pageRef.current.length === 2 && stateRef.current === 'REPLACE')) {
      return false;
    }
    for (let i = 0; i < pageRef.current.length; ++i) {
      if (pageRef.current[i]?.children?.['key'] === key || pageRef.current[i]?.key === key) {
        return true;
      }
    }
    return false;
  };

  const handleWindowResize = debounce(() => {
    //console.log('刷新了===========')
    if (pageRef.current.length) {
      stateRef.current = 'PUSH';
      setUpdate({});
    }
  }, 400);
  const updateWindowResize = () => {
    handleWindowResize();
  };
  useEffect(() => {
    window.removeEventListener('resize', updateWindowResize);
    window.addEventListener('resize', updateWindowResize);

    return () => {
      window.removeEventListener('resize', updateWindowResize);
    };
  }, [drawerVisible]);

  useEffect(() => {
    const nativeClickBack = () => {
      if (drawerVisible) {
        setDrawerVisible(false)
      } else if (pageRef.current.length) {
        pop()
      } else if (searchVisible) {
        setSearchVisible(false)
      } else if(walletVisible) {
        setWalletVisible(false)
      } else if(userCentervisible) {
        setUserCenterVisible(false)
      } else {
        window.history.back();
      }
    }
    document.removeEventListener("nativeClickBack", nativeClickBack)
    document.addEventListener('nativeClickBack', nativeClickBack);
    return () => {
      document.removeEventListener("nativeClickBack", nativeClickBack)
    }
  }, [drawerVisible, searchVisible,walletVisible,userCentervisible])

  // useEffect(() => {
  //   if (!drawerVisible && !searchVisible && !pageRef.current.length) {
  //     return
  //   }

  //   if (stateRef.current !== 'PUSH' && pageRef.current.length != 0) {
  //     return
  //   }

  //   const goBack = () => {
  //     if (drawerVisible) {
  //       setDrawerVisible(false)
  //     } else if (pageRef.current.length) {
  //       pop()
  //     } else if (searchVisible) {
  //       setSearchVisible(false)
  //     }
  //   }
  //   history.pushState(null, '', document.URL);
  //   //window.removeEventListener("popstate",goBack)
  //   window.addEventListener('popstate', goBack)
  //   return () => {
  //     window.removeEventListener("popstate", goBack)
  //   }
  // }, [drawerVisible, searchVisible, update])

  const clickBackgroundRef = useRef(false)
  function stopClose() {
    clickBackgroundRef.current = true;
  }
  const activityEffectRef = useRef<any>()
  function useActiveEffect (effect,name){
    if(!activityEffectRef.current) {
      activityEffectRef.current = {}
    }
    activityEffectRef.current[name] = effect
  }

  const passedContext: IFDialogPlusContext = {
    closeSelf: handleColseSlef,
    showBtnBack: hadnleShowBtnBack,
    closeAll: closeAll,
    pop: pop,
    push: push,
    replace: replace,
    enableKey: topKey,
    closeByNames: handleColseByNames,
    stopClose: stopClose,
    useActiveEffect: useActiveEffect
  };



  useEffect(() => {
    const syncPortalContainer = () => {
      setPortalContainer(getMobileCenteredShellPortalRoot());
    };
    const observer = new MutationObserver(syncPortalContainer);

    syncPortalContainer();
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => () => setDialogPlusVisible(false), []);

  useEffect(() => {
    setDialogPlusVisible(visible);
    if (!visible && maskClipRef.current) {
      if (topPresentation === 'page') {
        maskClipRef.current.style.transitionDuration = '0s';
        maskClipRef.current.style.width = '0px';
        maskClipRef.current.style.height = '0px';
      }
      maskClipRef.current.style.marginTop = '0px';
      maskClipRef.current.style.marginLeft = '0px';
      pageRef.current.length = 0;
    }
    if (!visible) {
      // Notify the modal orchestrator that all DialogPlus pages are closed
      window.dispatchEvent(new Event("dialog-plus-all-closed"));

      updateTopPageState(undefined);

      if(backGroundRef.current) {
        backGroundRef.current.style.zIndex = 'unset';
      }

    }
  }, [visible, topPresentation]);

  useEffect(() => {
    document.body.dataset.mobileCenteredShellDialogActive =
      useShellPageLayout && visible && topPresentation === 'page' ? 'true' : 'false';

    return () => {
      delete document.body.dataset.mobileCenteredShellDialogActive;
    };
  }, [topPresentation, useShellPageLayout, visible]);

  return (
    <Portal
      key={portalContainer?.id || 'body'}
      visible={visible}
      forceRender={true}
      getContainer={() => portalContainer || document.body}
    >
      <CSSTransition
        nodeRef={maskRef}
        in={visible}
        timeout={400}
        appear
        mountOnEnter={false}
        classNames="fadedialog"
        unmountOnExit={false}
        onEnter={() => {
          if (maskRef.current) maskRef.current.style.display = 'block';
        }}
        onExited={() => {
          if (maskRef.current) maskRef.current.style.display = 'none';
          handleMaskAnimationEnd();
        }}
      >
        <div ref={maskRef} aria-hidden className={`${prefixCls} ${prefixCls}-mask`} data-dialog-presentation={topPresentation} onMouseUp={() => {
          setTimeout(() => {
            clickBackgroundRef.current = false;
          }, 16);
        }} onClick={() => {
          if (!clickBackgroundRef.current) {
            pop()
          }
        }}>
          <DialogPlusContext.Provider value={passedContext}>
            <div className={classNames([`${prefixCls}-clip-mask`])} data-dialog-presentation={topPresentation} ref={maskClipRef}>
             <div className='dialog-background' ref={backGroundRef}></div>
              {pageRef.current?.map((v, index) => {
                if (v.key && !v.children) {
                  return (
                    <v.lazyCompoent
                      key={v.key}
                      presentation={v.presentation}
                      {...v.props}
                    />
                  );
                } else {
                  return (
                    <React.Fragment key={v.key || index}>
                      {v?.children}
                    </React.Fragment>
                  );
                }
              })}
            </div>
          </DialogPlusContext.Provider>
        </div>
      </CSSTransition>
    </Portal>
  );
}
export default forwardRef(DialogPlus);
